import {
  ImportHeaderStatusName,
  ImportDetailStatusName,
  ImportStatusId,
} from '../interfaces/Import/ShipmentBulkUploadApi'

/**
 * Mapeo de estados de carga masiva a etiquetas (es) y clases de badge (Tailwind),
 * más mapeo de códigos de error a mensajes UX y clasificación transitorio/dato.
 *
 * Calibrado a DESIGN.md (tokens semánticos):
 *   InProcess → main (azul env-aware) · Finalized → success #16a34a
 *   FinalizedWithErrors → warning #f59e0b · Cancelled → gray · Deleted → error #dc2626
 *   Executed (detalle) → success · Error → error · Pending → gray
 *
 * Resolución por `status_name` (estable); el `status_id` numérico (68–75) es solo fallback,
 * porque los IDs son placeholders del backend y pueden colisionar con [config].[Status].
 */

export type StatusTone = 'info' | 'success' | 'warning' | 'error' | 'neutral'

export interface StatusMeta {
  label: string
  /** Clases Tailwind para el badge (pill). */
  badgeClass: string
  tone: StatusTone
}

const HEADER_META: Record<string, StatusMeta> = {
  [ImportHeaderStatusName.InProcess]: {
    label: 'En proceso',
    badgeClass: 'bg-main-50 text-main-600',
    tone: 'info',
  },
  [ImportHeaderStatusName.Finalized]: {
    label: 'Finalizado',
    badgeClass: 'bg-green-50 text-green-700',
    tone: 'success',
  },
  [ImportHeaderStatusName.FinalizedWithErrors]: {
    label: 'Finalizado con errores',
    badgeClass: 'bg-amber-50 text-amber-700',
    tone: 'warning',
  },
  [ImportHeaderStatusName.Cancelled]: {
    label: 'Cancelado',
    badgeClass: 'bg-gray-100 text-gray-600',
    tone: 'neutral',
  },
  [ImportHeaderStatusName.Deleted]: {
    label: 'Eliminado',
    badgeClass: 'bg-red-50 text-red-700',
    tone: 'error',
  },
}

const DETAIL_META: Record<string, StatusMeta> = {
  [ImportDetailStatusName.Pending]: {
    label: 'Pendiente',
    badgeClass: 'bg-gray-100 text-gray-600',
    tone: 'neutral',
  },
  [ImportDetailStatusName.Executing]: {
    label: 'En ejecución',
    badgeClass: 'bg-main-50 text-main-600',
    tone: 'info',
  },
  [ImportDetailStatusName.Executed]: {
    label: 'OK',
    badgeClass: 'bg-green-50 text-green-700',
    tone: 'success',
  },
  [ImportDetailStatusName.Error]: {
    label: 'Error',
    badgeClass: 'bg-red-50 text-red-700',
    tone: 'error',
  },
  [ImportDetailStatusName.Substituted]: {
    label: 'Sustituida',
    badgeClass: 'bg-gray-100 text-gray-600',
    tone: 'neutral',
  },
  [ImportDetailStatusName.ErrorProcessing]: {
    label: 'Error procesando',
    badgeClass: 'bg-red-50 text-red-700',
    tone: 'error',
  },
  [ImportDetailStatusName.ErrorServer]: {
    label: 'Error servidor',
    badgeClass: 'bg-amber-50 text-amber-700',
    tone: 'warning',
  },
}

/** Fallback por ID numérico → nombre interno (si el backend no manda status_name). */
const HEADER_ID_TO_NAME: Record<number, string> = {
  [ImportStatusId.HeaderInProcess]: ImportHeaderStatusName.InProcess,
  [ImportStatusId.HeaderFinalized]: ImportHeaderStatusName.Finalized,
  [ImportStatusId.HeaderFinalizedWithErrors]:
    ImportHeaderStatusName.FinalizedWithErrors,
  [ImportStatusId.HeaderCancelled]: ImportHeaderStatusName.Cancelled,
  [ImportStatusId.HeaderDeleted]: ImportHeaderStatusName.Deleted,
}

const DETAIL_ID_TO_NAME: Record<number, string> = {
  [ImportStatusId.DetailPending]: ImportDetailStatusName.Pending,
  [ImportStatusId.DetailExecuting]: ImportDetailStatusName.Executing,
  [ImportStatusId.DetailExecuted]: ImportDetailStatusName.Executed,
  [ImportStatusId.DetailError]: ImportDetailStatusName.Error,
  [ImportStatusId.DetailSubstituted]: ImportDetailStatusName.Substituted,
  [ImportStatusId.DetailErrorProcessing]:
    ImportDetailStatusName.ErrorProcessing,
  [ImportStatusId.DetailErrorServer]: ImportDetailStatusName.ErrorServer,
}

// Decisión de producto: un estatus que el front no supo decodificar se muestra como
// 'Pendiente', no como 'Desconocido' — para el operador la carga recién entrada todavía
// no pasó por nada, y "Desconocido" se leía como error.
// El costo asumido: colisiona con el 'Pendiente' real de DETAIL_META, así que este label
// no distingue "pendiente de verdad" de "no lo pude decodificar", ni en pantalla ni en el
// Excel que exporta el listado. Si alguna vez hace falta separarlos, el lugar es acá.
const UNKNOWN_META: StatusMeta = {
  label: 'Pendiente',
  badgeClass: 'bg-gray-100 text-gray-600',
  tone: 'neutral',
}

export function headerStatusMeta(
  statusName?: string | null,
  statusId?: number | null,
): StatusMeta {
  if (statusName && HEADER_META[statusName]) return HEADER_META[statusName]
  if (statusId != null && HEADER_ID_TO_NAME[statusId]) {
    return HEADER_META[HEADER_ID_TO_NAME[statusId]]
  }
  return UNKNOWN_META
}

/**
 * Estatus canónico del header, con el MISMO fallback por `status_id` que usa
 * `headerStatusMeta` para pintar el badge.
 *
 * Existe porque no tenerlo causó un bug real: la pantalla mostraba "Finalizado"
 * (el badge cae al `status_id` cuando el `status_name` no matchea) mientras los
 * booleanos de las acciones comparaban el string crudo sin fallback y daban
 * `false`. Resultado: la carga se veía finalizada y el botón de crear manifiesto
 * no aparecía nunca.
 *
 * Regla: cualquier decisión sobre el estatus del header pasa por acá. Si el badge
 * y un botón no coinciden, uno de los dos miente.
 */
export function resolveHeaderStatusName(
  statusName?: string | null,
  statusId?: number | null,
): ImportHeaderStatusName | null {
  if (statusName && HEADER_META[statusName]) {
    return statusName as ImportHeaderStatusName
  }
  if (statusId != null && HEADER_ID_TO_NAME[statusId]) {
    return HEADER_ID_TO_NAME[statusId] as ImportHeaderStatusName
  }
  return null
}

/**
 * Estados CERRADOS de la cabecera: el backend rechaza cualquier modificación sobre
 * ellos ('shipping.ShipmentBulkUploadHeader.2' — "ya fue finalizada o cancelada").
 *
 * Existe porque no tenerlo causó un bug real: una carga en Eliminado seguía
 * mostrando "Reintentar" y "Corregir errores", porque esos botones se decidían
 * solo con los estatus de los DETALLES (que conservan sus 75/77/78 después de
 * eliminar la cabecera) sin mirar la cabecera. El operador apretaba y el backend
 * devolvía un error que no explicaba nada.
 *
 * Regla: sobre una cabecera cerrada la única acción que queda es descargar el
 * archivo original — no se reintenta, no se corrige, no se elimina.
 */
const CLOSED_HEADER_STATUSES = new Set<string>([
  ImportHeaderStatusName.Finalized,
  ImportHeaderStatusName.Cancelled,
  ImportHeaderStatusName.Deleted,
])

/**
 * `true` si la cabecera está en un estado terminal (Finalizado / Cancelado /
 * Eliminado). Un estatus que no se pudo decodificar devuelve `false`: ante la
 * duda no se esconden acciones, se dejan como estaban.
 */
export function isClosedHeaderStatus(
  statusName?: string | null,
  statusId?: number | null,
): boolean {
  const s = resolveHeaderStatusName(statusName, statusId)
  return s != null && CLOSED_HEADER_STATUSES.has(s)
}

export function detailStatusMeta(
  statusName?: string | null,
  statusId?: number | null,
): StatusMeta {
  if (statusName && DETAIL_META[statusName]) return DETAIL_META[statusName]
  if (statusId != null && DETAIL_ID_TO_NAME[statusId]) {
    return DETAIL_META[DETAIL_ID_TO_NAME[statusId]]
  }
  return UNKNOWN_META
}

/**
 * Resuelve el nombre canónico del estado de detalle con el mismo fallback name→id
 * que usa `detailStatusMeta`. Necesario para decidir acciones (ver guía / ver payload /
 * eliminar) y contar ejecutados/errores: comparar el `status_name` crudo contra el enum
 * falla cuando el backend manda un name distinto y solo coincide por `status_id`.
 */
export function resolveDetailStatusName(
  statusName?: string | null,
  statusId?: number | null,
): ImportDetailStatusName | null {
  if (statusName && DETAIL_META[statusName]) {
    return statusName as ImportDetailStatusName
  }
  if (statusId != null && DETAIL_ID_TO_NAME[statusId]) {
    return DETAIL_ID_TO_NAME[statusId] as ImportDetailStatusName
  }
  return null
}

// ---------------------------------------------------------------------------
// Reglas de acciones de carga masiva (alineadas al backend)
//
// Los botones se calculan agregando los `status_id` de los detalles (la spec exige
// que el front no habilite lo que el backend rechazaría). Estatus de detalle:
//   73 Pendiente · 74 Ejecutando/OK · 75 Error · 76 Sustituida · 77 Error procesando ·
//   78 Error servidor · 79 En ejecución (procesándose ahora)
// ---------------------------------------------------------------------------

/** Tope de reintentos del backend para errores de servidor (78); se muestra "intento N/40". */
export const SERVER_ERROR_RETRY_CAP = 40

/**
 * Números de guía TEALCA de los detalles que efectivamente crearon una guía.
 *
 * Es la ÚNICA definición del universo de guías de una carga masiva: la usan el
 * botón de crear manifiesto, el de imprimir etiquetas, el conteo del aviso de
 * cobertura y la preselección del modal de despacho.
 *
 * Ojo con la diferencia contra `getBulkActionFlags().executedCount`: ese cuenta
 * FILAS en estado Executed, incluidas las que por lo que sea no tienen
 * `shipment_number`. Acá se necesita el número, no la fila: una guía sin número
 * no se puede preseleccionar, ni incluir, ni imprimir. Si se usara
 * `executedCount` como universo, el aviso "vas a incluir N de M" mentiría.
 */
export function executedGuideNumbers(
  details: Array<{
    status_name?: string | null
    status_id?: number | null
    shipment_number?: string | null
  }>,
): string[] {
  if (!Array.isArray(details)) return []

  return details
    .filter(
      (d) =>
        resolveDetailStatusName(d?.status_name, d?.status_id) ===
        ImportDetailStatusName.Executed,
    )
    .map((d) => d?.shipment_number?.trim() ?? '')
    .filter((n) => n.length > 0)
}

/**
 * Un detalle se puede eliminar salvo cuando ya generó el envío real (74 Ejecutando/OK)
 * o está creándolo justo ahora (79 En ejecución): en ambos casos borrarlo dejaría estado
 * inconsistente contra el backend.
 */
export function canDeleteDetailStatus(
  statusName?: string | null,
  statusId?: number | null,
): boolean {
  const s = resolveDetailStatusName(statusName, statusId)
  return (
    s !== ImportDetailStatusName.Executed &&
    s !== ImportDetailStatusName.Executing
  )
}

export interface BulkActionFlags {
  pendingCount: number
  /** Detalles procesándose ahora mismo (79). */
  runningCount: number
  /** Detalles que ya generaron el envío real (74). */
  executedCount: number
  substitutedCount: number
  /** Errores de dato que el usuario corrige (75 + 77). */
  dataErrorCount: number
  /** Errores de servidor reintentables (78). */
  serverErrorCount: number
  hasExecuting: boolean
  /** Hay 75/77 y la cabecera está abierta → habilita "Corregir errores". */
  canEditErrors: boolean
  /** Hay 78, NINGÚN 75/77 y la cabecera está abierta → habilita "Reintentar". Mutuamente excluyente con canEditErrors. */
  canRetry: boolean
  /** Cabecera 70 (siempre) o 68 sin ningún 74. */
  canDeleteHeader: boolean
  /**
   * Cabecera en estado terminal (69 Finalizado / 71 Cancelado / 72 Eliminado).
   * Las acciones sobre errores no solo se deshabilitan: no se muestran, porque
   * sobre una carga cerrada nunca van a volver a aplicar.
   */
  isHeaderClosed: boolean
}

/**
 * Deriva el estado de los botones de la carga masiva desde los estatus de sus detalles
 * más el estatus de la cabecera. Puro y testeable (las tablas de verdad de la spec son
 * los casos de prueba). Resuelve cada detalle con el mismo fallback name→id del resto.
 */
export function getBulkActionFlags(
  details: Array<{ status_name?: string | null; status_id?: number | null }>,
  headerStatusName?: string | null,
  headerStatusId?: number | null,
): BulkActionFlags {
  let pendingCount = 0
  let runningCount = 0
  let executedCount = 0
  let substitutedCount = 0
  let dataErrorCount = 0
  let serverErrorCount = 0

  for (const d of details) {
    switch (resolveDetailStatusName(d.status_name, d.status_id)) {
      case ImportDetailStatusName.Pending:
        pendingCount++
        break
      case ImportDetailStatusName.Executing:
        runningCount++
        break
      case ImportDetailStatusName.Executed:
        executedCount++
        break
      case ImportDetailStatusName.Substituted:
        substitutedCount++
        break
      case ImportDetailStatusName.Error:
      case ImportDetailStatusName.ErrorProcessing:
        dataErrorCount++
        break
      case ImportDetailStatusName.ErrorServer:
        serverErrorCount++
        break
      default:
        break
    }
  }

  const hasExecuting = executedCount > 0

  // Estatus de cabecera con el mismo fallback name→id que headerStatusMeta.
  const headerName =
    (headerStatusName && HEADER_META[headerStatusName]
      ? headerStatusName
      : headerStatusId != null
        ? HEADER_ID_TO_NAME[headerStatusId]
        : null) ?? null
  const isInProcess = headerName === ImportHeaderStatusName.InProcess
  const isFinalizedWithErrors =
    headerName === ImportHeaderStatusName.FinalizedWithErrors
  // Cabecera cerrada (69/71/72): los detalles conservan sus errores, pero el
  // backend ya no acepta ni reintento ni corrección sobre esta carga.
  const isHeaderClosed = headerName != null && CLOSED_HEADER_STATUSES.has(headerName)

  return {
    pendingCount,
    runningCount,
    executedCount,
    substitutedCount,
    dataErrorCount,
    serverErrorCount,
    hasExecuting,
    canEditErrors: dataErrorCount > 0 && !isHeaderClosed,
    canRetry: serverErrorCount > 0 && dataErrorCount === 0 && !isHeaderClosed,
    canDeleteHeader:
      isFinalizedWithErrors ||
      (isInProcess && !hasExecuting && runningCount === 0),
    isHeaderClosed,
  }
}

// ---------------------------------------------------------------------------
// Mapeo de códigos de error → mensajes UX (Frontend Spec sección 7)
// ---------------------------------------------------------------------------

export const IMPORT_ERROR_MESSAGES: Record<string, string> = {
  'shipping.ShipmentBulkUploadHeader.1':
    'La carga masiva no existe o fue eliminada',
  'shipping.ShipmentBulkUploadHeader.2':
    'Esta carga masiva ya fue finalizada o cancelada, no se puede modificar',
  'shipping.ShipmentBulkUploadHeader.3':
    'Ya existe una carga masiva con ese número',
  'shipping.ShipmentBulkUploadHeader.4':
    'La carga masiva debe tener al menos una guía',
  'shipping.ShipmentBulkUploadHeader.5':
    'No se pudo guardar el archivo en la nube',
  'shipping.ShipmentBulkUploadHeader.6':
    'Esta carga masiva no tiene archivo asociado',
  'shipping.ShipmentBulkUploadHeader.7':
    'Debes subir un archivo o indicar el motivo de la carga masiva',
  'shipping.ShipmentBulkUploadHeader.8':
    'El archivo supera los 25 MB permitidos',
  'shipping.ShipmentBulkUploadHeader.9':
    'El sistema de procesamiento no está disponible. Reintentá en unos minutos.',
  'shipping.ShipmentBulkUploadHeader.10':
    'Tipo de archivo no soportado. Subí CSV, Excel o JSON.',
  'shipping.ShipmentBulkUploadHeader.11':
    'No tenés permisos para acceder a esta carga masiva',
  'shipping.ShipmentBulkUploadHeader.12':
    'La carga masiva debe referenciar un sitio de cuenta o una cuenta',
  'shipping.ShipmentBulkUploadDetail.1': 'El detalle no existe',
  'shipping.ShipmentBulkUploadDetail.2':
    'Esta guía ya fue procesada y no se puede modificar',
  // Vincular carga masiva ↔ manifiesto. El manifiesto se crea en el API .NET y
  // Django valida que exista antes de guardar el vínculo; si no lo encuentra, el
  // manifiesto YA está creado del otro lado y lo único que falta es el vínculo.
  'logistics.MasterShipmentHeader.1':
    'El manifiesto no se encontró del lado del sistema de cargas masivas. El manifiesto sí se creó: reintentá vincularlo.',
  'general.General.5': 'Hay datos inválidos. Revisá los campos marcados.',
}

/** Mensaje UX para un code del envelope; cae al fallback si no está mapeado. */
export function messageForCode(
  code?: string | null,
  fallback = 'Ocurrió un error. Intentá de nuevo.',
): string {
  if (code && IMPORT_ERROR_MESSAGES[code]) return IMPORT_ERROR_MESSAGES[code]
  return fallback
}

// ---------------------------------------------------------------------------
// error_data → errores por campo/fila (general.General.5: validación pydantic)
//
// El backend devuelve claves tipo "shipment_list.0.consignee.phone". Las traducimos
// al índice de grupo + la key de ShipmentGroupErrors para pintar el campo exacto en
// el Review en lugar de un modal genérico. Mapeo conservador: lo que no reconocemos
// se devuelve en `unmapped` para mostrarlo agregado (no se pierde información).
// ---------------------------------------------------------------------------

/** path del backend (luego de `shipment_list.<i>.`) → key de ShipmentGroupErrors. */
const SERVER_FIELD_TO_GROUP_KEY: Record<string, string> = {
  'consignee.phone': 'consigneePhone',
  'consignee.identification': 'consigneeDocNumber',
  'consignee.name': 'consigneeName',
  'consignee.email': 'consigneeEmail',
  'destination.address.line': 'destinationAddress',
  'destination.location': 'destinationCity',
  'destination.store': 'storeCode',
  partner_shipment_id: 'shipmentHeaderReference',
  'shipper.name': 'shipperName',
  'shipper.identification': 'shipperDocNumber',
  // shipper.phone NO se mapea a propósito: viene de user.client y no es editable
  // por fila, así que cae en `unmapped` para evitar un error fantasma que bloquee
  // el envío sin un campo donde corregirlo.
}

/** Traduce el mensaje crudo del backend a algo legible en español. */
function translateServerFieldMessage(path: string, rawMsg: string): string {
  const cleaned = (rawMsg || '').replace(/^Value error,\s*/i, '').trim()
  if (path.endsWith('.phone')) {
    const phone = /(\+\d{1,3} \d+)/.exec(rawMsg)?.[1] || ''
    return phone
      ? `Teléfono inválido (${phone}). Debe tener entre 9 y 15 dígitos.`
      : 'Teléfono inválido. Debe tener entre 9 y 15 dígitos.'
  }
  return cleaned || 'Dato inválido'
}

/**
 * Errores de EJECUCIÓN, los que trae `error_message` de cada detalle.
 *
 * Ojo con la diferencia contra `mapServerFieldErrors`: aquel traduce el
 * `error_data` que devuelve el POST/PUT al ENVIAR (validación de esquema, con
 * rutas tipo `shipment_list.0.consignee.phone`). Este traduce el mensaje que el
 * backend guardó cuando INTENTÓ crear la guía y falló contra una regla de
 * negocio. Llegan por caminos distintos y con formatos distintos.
 *
 * El formato es `<code>: <mensaje en prosa>`, ej.:
 *
 *   shipping.ShipmentHeader.37: El valor declarado ($15.0) de la guía 0 no puede
 *   ser menor a $53.00 para el peso total facturado 1.0
 *
 * Sin traducir, ese texto solo aparece en la tabla del detalle: el operador entra
 * a Corregir errores y ve 10 guías sin saber cuál falló ni por qué. Traducirlo a
 * un error POR CAMPO lo pone en la sección que corresponde, en la guía que
 * corresponde.
 */

/** Extrae el primer monto en dólares del mensaje, ej. "$53.00" → "$53.00". */
const firstAmount = (msg: string): string | null => {
  // El mensaje trae DOS montos: el declarado y el mínimo. Se busca el que sigue a
  // "menor a", no el primero de la oración, que es el valor que puso el operador.
  const afterMenorA = /menor a\s*\$?\s*([\d.,]+)/i.exec(msg)
  if (afterMenorA) return `$${afterMenorA[1]}`
  return null
}

interface ExecutionErrorRule {
  /** Código del backend, si lo trae. */
  code?: string
  /** Respaldo por texto, para cuando el código cambia o no viene. */
  test: RegExp
  /** Key de `ShipmentGroupErrors` donde se muestra. */
  groupKey: string
  toMessage: (raw: string) => string
}

const EXECUTION_ERROR_RULES: ExecutionErrorRule[] = [
  {
    // Valor declarado por debajo del mínimo para el peso facturado.
    code: 'shipping.ShipmentHeader.37',
    // `[\s\S]*?` y no `.*`: sin el flag `s` (no compila bajo este target de TS) el
    // punto no cruza saltos de línea, y el backend a veces los mete.
    test: /valor declarado[\s\S]*?(no puede ser menor|menor a)/i,
    // Se muestra en "Piezas": el valor declarado se edita ahí, no en la cabecera.
    groupKey: 'pieces',
    toMessage: (raw) => {
      const min = firstAmount(raw)
      return min
        ? `El total del valor declarado no puede ser menor a ${min}`
        : 'El total del valor declarado es menor al mínimo permitido'
    },
  },
]

/**
 * Traduce el `error_message` de un detalle a errores por campo.
 *
 * Devuelve `{}` si no matchea ninguna regla conocida: el mensaje crudo sigue
 * visible en la tabla del detalle, así que no se pierde información — solo no se
 * ancla a un campo.
 */
export function mapDetailExecutionError(
  errorMessage?: string | null,
): Record<string, string> {
  const raw = (errorMessage || '').trim()
  if (!raw) return {}

  // El código viene como prefijo `codigo: mensaje`; puede no venir.
  // `[\s\S]*` en vez de `.` con flag `s`: el target de TS de este proyecto es
  // anterior a ES2018 y el flag `s` no compila.
  const codeMatch = /^([a-z]+(?:\.[A-Za-z]+)*\.\d+)\s*:\s*([\s\S]*)$/.exec(raw)
  const code = codeMatch?.[1]
  const body = codeMatch?.[2]?.trim() || raw

  for (const rule of EXECUTION_ERROR_RULES) {
    const matches =
      (rule.code && code === rule.code) ||
      rule.test.test(body) ||
      rule.test.test(raw)
    if (matches) return { [rule.groupKey]: rule.toMessage(raw) }
  }

  return {}
}

export interface ServerFieldErrors {
  /** índice de grupo → { keyDeShipmentGroupErrors → mensaje } */
  byIndex: Record<number, Record<string, string>>
  /** mensajes que no se pudieron asociar a un campo editable */
  unmapped: string[]
}

/**
 * Convierte el `error_data` del envelope en errores por fila/campo para el Review.
 * Acepta claves "shipment_list.<i>.<path>"; cualquier otra forma cae en `unmapped`.
 */
export function mapServerFieldErrors(
  errorData?: Record<string, any> | null,
): ServerFieldErrors {
  const byIndex: Record<number, Record<string, string>> = {}
  const unmapped: string[] = []
  if (!errorData || typeof errorData !== 'object') return { byIndex, unmapped }

  for (const [rawKey, rawVal] of Object.entries(errorData)) {
    const rawMsg = typeof rawVal === 'string' ? rawVal : JSON.stringify(rawVal)
    const m = /^shipment_list\.(\d+)\.(.+)$/.exec(rawKey)
    if (!m) {
      unmapped.push(rawMsg)
      continue
    }
    const idx = parseInt(m[1], 10)
    const path = m[2]
    const groupKey = SERVER_FIELD_TO_GROUP_KEY[path]
    const message = translateServerFieldMessage(path, rawMsg)
    if (!groupKey) {
      unmapped.push(message)
      continue
    }
    byIndex[idx] = { ...(byIndex[idx] || {}), [groupKey]: message }
  }

  return { byIndex, unmapped }
}

// ---------------------------------------------------------------------------
// Clasificación de errores: transitorio (auto-retry) vs dato (humano)
// ---------------------------------------------------------------------------

export type ImportErrorKind = 'transient' | 'data'

/**
 * Códigos claramente transitorios — se reintentan solos (con cap + backoff aguas arriba).
 * Conservador a propósito: un error desconocido NO es transitorio (se trata como 'data'
 * para evitar loops de reintento contra un backend que falla por una causa real).
 *
 * Nota (dependencia backend abierta): los errores por detalle llegan hoy como string
 * (`error_message`), no como code estructurado. Hasta que el backend exponga un code/categoría
 * por detalle, la clasificación de detalles es match de string y debe quedar conservadora.
 */
const TRANSIENT_CODES = new Set<string>([
  'shipping.ShipmentBulkUploadHeader.9', // cola Redis / procesamiento no disponible
  'shipping.ShipmentBulkUploadHeader.5', // fallo al subir archivo a Spaces
])

const TRANSIENT_MESSAGE_PATTERNS: RegExp[] = [
  /\btimeout\b/i,
  /\btemporarily unavailable\b/i,
  /\bservice unavailable\b/i,
  /\bconnection (refused|reset|error)\b/i,
  /\b50[0234]\b/, // 500/502/503/504
  /\bredis\b/i,
  /\bqueue\b/i,
]

export function classifyImportError(
  code?: string | null,
  message?: string | null,
): ImportErrorKind {
  if (code && TRANSIENT_CODES.has(code)) return 'transient'
  if (message && TRANSIENT_MESSAGE_PATTERNS.some((re) => re.test(message))) {
    return 'transient'
  }
  return 'data'
}
